import { maxBy } from "lodash-es";
import { FC, useMemo } from "react";

import { SessionStatsCard } from "./SessionStatsCard";

import { formatLocalePattern } from "i18n/helpers";
import { t } from "i18n/i18n";
import { CalendarIcon, ClockIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId, selectFilteredClassSessionsByDateRange } from "store/selectors";
import { useListSelector } from "store/store-hooks";

type SessionsCardProps = {
  dateRangeValue: number;
};

export const SessionsCard: FC<SessionsCardProps> = ({ dateRangeValue }) => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const { canAuthorSection } = useCapabilities();
  const sessions = useListSelector(
    (state) => selectFilteredClassSessionsByDateRange(state, dateRangeValue),
    ["started_at"],
  );

  const sessionCount = useMemo(() => sessions.length, [sessions]);
  const lastSessionDateString = useMemo(() => {
    const lastSessionDate = maxBy(sessions, "started_at")?.started_at;
    return lastSessionDate
      ? formatLocalePattern(lastSessionDate, "common.formats.month_day_year")
      : "";
  }, [sessions]);

  if (!canAuthorSection || !sectionId) return null;

  const lastSessionText =
    sessionCount > 0
      ? t("session_log_view.stats.sessions.last_session", {
          date: lastSessionDateString,
        })
      : t("session_log_view.stats.sessions.no_sessions");

  return (
    <SessionStatsCard
      FooterIcon={ClockIcon}
      footerText={lastSessionText}
      kind="sessions"
      title={t("session_log_view.stats.sessions.title")}
      TitleIcon={CalendarIcon}
      value={sessionCount}
      valueDescription={t("session_log_view.stats.sessions.value_description")}
    />
  );
};
